import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function Home() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentFeatured, setCurrentFeatured] = useState(0);
  const [filteredArticles, setFilteredArticles] = useState(articles);

  const featuredArticles = articles.filter((a) => a.isFeatured);
  const hotArticles = articles.filter((a) => a.isHot).slice(0, 5);

  useEffect(() => {
    document.title = '黑料天天动漫 - 最新动漫黑料爆料平台 | 动漫圈内幕资讯';
  }, []);

  useEffect(() => {
    let result = articles;
    if (activeCategory !== 'all') {
      result = result.filter((a) => a.category === activeCategory);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.excerpt.toLowerCase().includes(query) ||
          a.tags.some((t) => t.toLowerCase().includes(query))
      );
    }
    setFilteredArticles(result);
  }, [activeCategory, searchQuery]);

  useEffect(() => {
    if (featuredArticles.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentFeatured((prev) => (prev + 1) % featuredArticles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredArticles.length]);

  const nextFeatured = useCallback(() => {
    setCurrentFeatured((prev) => (prev + 1) % featuredArticles.length);
  }, [featuredArticles.length]);

  const prevFeatured = useCallback(() => {
    setCurrentFeatured((prev) => (prev - 1 + featuredArticles.length) % featuredArticles.length);
  }, [featuredArticles.length]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料天天动漫',
    url: 'https://heimanime.com',
    description: '黑料天天动漫为你汇聚最新、最劲爆的动漫圈黑料资讯，涵盖动漫声优、制作公司、IP改编等幕后爆料内容。',
    email: 'heimanime@163.com',
    telephone: '010-8888-6666',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黑料天天动漫',
    url: 'https://heimanime.com',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://heimanime.com/?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <Navbar />

      <main>
        {/* Hero / Featured Section */}
        <section className="pt-16">
          {featuredArticles.length > 0 && (
            <div className="relative w-full overflow-hidden">
              <ArticleCard
                key={featuredArticles[currentFeatured].id}
                {...featuredArticles[currentFeatured]}
                variant="featured"
              />
              {featuredArticles.length > 1 && (
                <>
                  <button
                    onClick={prevFeatured}
                    className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm text-background-50 transition-all cursor-pointer z-10"
                    aria-label="上一个"
                  >
                    <i className="ri-arrow-left-s-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextFeatured}
                    className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 backdrop-blur-sm text-background-50 transition-all cursor-pointer z-10"
                    aria-label="下一个"
                  >
                    <i className="ri-arrow-right-s-line text-xl"></i>
                  </button>
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {featuredArticles.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentFeatured(i)}
                        className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === currentFeatured ? 'bg-accent-500 w-6' : 'bg-background-50/40 hover:bg-background-50/60'}`}
                        aria-label={`第 ${i + 1} 条`}
                      ></button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* Category Tabs */}
        <section className="py-6 border-b border-background-200/70">
          <div className="container-custom">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setActiveCategory('all')}
                className={`whitespace-nowrap px-5 py-2 text-sm font-medium rounded-full transition-all cursor-pointer ${activeCategory === 'all' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'}`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`whitespace-nowrap px-5 py-2 text-sm font-medium rounded-full transition-all cursor-pointer flex items-center gap-1.5 ${activeCategory === cat.slug ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'}`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-12">
          <div className="container-custom">
            {searchQuery && (
              <div className="mb-8 p-4 rounded-lg bg-accent-50 border border-accent-200">
                <p className="text-sm text-foreground-700">
                  搜索 "<strong className="text-primary-600">{searchQuery}</strong>" 的结果，共找到 <strong>{filteredArticles.length}</strong> 篇文章
                </p>
              </div>
            )}

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Article Grid */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    {activeCategory === 'all' ? '最新爆料' : categories.find((c) => c.slug === activeCategory)?.name || '最新爆料'}
                  </h2>
                </div>

                {filteredArticles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {filteredArticles.map((article) => (
                      <ArticleCard key={article.id} {...article} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">暂无相关文章</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                {/* Hot Articles */}
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-fire-line text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900">热门爆料</h3>
                  </div>
                  <div className="space-y-1">
                    {hotArticles.map((article) => (
                      <ArticleCard key={article.id} {...article} variant="compact" />
                    ))}
                  </div>
                </div>

                {/* Newsletter */}
                <div className="bg-gradient-to-br from-accent-500 to-primary-500 rounded-xl p-6 text-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/20 mb-3">
                    <i className="ri-mail-send-line text-xl"></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold mb-2">订阅黑料速递</h3>
                  <p className="text-sm text-background-50/80 mb-4">
                    第一时间获取最新动漫圈爆料资讯，不错过任何劲爆内幕！
                  </p>
                  <form
                    data-readdy-form="d9qsennnahb7382oveq0"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                      if (honeypot) {
                        (form.querySelector('.newsletter-success') as HTMLElement)!.style.display = 'block';
                        (form.querySelector('.newsletter-form-fields') as HTMLElement)!.style.display = 'none';
                        return;
                      }
                      const formData = new FormData(form);
                      try {
                        const res = await fetch('https://readdy.ai/api/form/d9qsennnahb7382oveq0', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const text = await res.text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        if (res.ok && parsed?.code === 'OK') {
                          (form.querySelector('.newsletter-success') as HTMLElement)!.style.display = 'block';
                          (form.querySelector('.newsletter-form-fields') as HTMLElement)!.style.display = 'none';
                        } else {
                          const msg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试';
                          const errEl = form.querySelector('.newsletter-error') as HTMLElement;
                          errEl!.textContent = msg;
                          errEl!.style.display = 'block';
                        }
                      } catch {
                        const errEl = form.querySelector('.newsletter-error') as HTMLElement;
                        errEl!.textContent = '网络错误，请检查连接后重试';
                        errEl!.style.display = 'block';
                      }
                    }}
                  >
                    <div className="newsletter-form-fields">
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="输入你的邮箱地址"
                        className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 text-foreground-900 placeholder-foreground-400 border-0 mb-3"
                      />
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-honeypot"
                      />
                      <button
                        type="submit"
                        className="w-full py-2.5 text-sm font-medium rounded-lg bg-foreground-950 hover:bg-foreground-900 text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        立即订阅
                      </button>
                      <p className="newsletter-error text-xs text-background-50 mt-2 hidden"></p>
                    </div>
                    <div className="newsletter-success text-center py-4" style={{ display: 'none' }}>
                      <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center rounded-full bg-background-50/20">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-sm font-medium">订阅成功！</p>
                      <p className="text-xs text-background-50/80 mt-1">感谢订阅，我们会第一时间推送最新爆料</p>
                    </div>
                  </form>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}